import { Box, Stack, Typography } from "@mui/material";
import { LoadingScreen } from "components/LoadingScreen";
import { DeleteModal, Notification } from "components/UI";
import { MutationError } from "components/UI/MutationError";
import { GOOGLE_API_KEY } from "config";
import i18next from "i18next";
import { useCallback, useEffect } from "react";
import { usePlacesWidget } from "react-google-autocomplete";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCentralStore } from "stores";
import { DenominationProfieHeader } from "../components";
import ChildStructuresSection from "../components/ChildStructuresSection";
import StructureDetailAddressSection from "../components/StructureDetailAddressSection";
import StructureDetailDescriptionSection from "../components/StructureDetailDescriptionSection";
import StructureDetailFormFooter from "../components/StructureDetailFormFooter";
import StructureDetailProfileSection from "../components/StructureDetailProfileSection";
import { useStructureDetailAddressQueries } from "../hooks/useStructureDetailAddressQueries";
import { useStructureDetailForm } from "../hooks/useStructureDetailForm";

export function StructureDetail() {
  const navigate = useNavigate();

  // Store hooks
  const setHeaderTitle = useCentralStore(
    useCallback(state => state.setHeaderTitle, [])
  );
  const setHeaderBackground = useCentralStore(
    useCallback(state => state.setHeaderBackground, [])
  );
  const openDeleteModal = useCentralStore(
    useCallback(state => state.openDeleteModal, [])
  );

  // Custom hooks
  const {
    register,
    handleSubmit,
    errors,
    defaultValues,
    reset,
    watch,
    setValue,
    structureTypeOptions,
    parentStructureOptions,
    childStructures,
    isCreatedByUser,
    isLoadingStructureTypes,
    isLoadingDenominations,
    isLoadingChildStructures,
    structureQuery,
    structureMutation,
    structureDeleteMutation,
    denominationId,
    onSubmit,
    handleDelete,
    control
  } = useStructureDetailForm();

  const { t } = useTranslation();

  const addressQueries = useStructureDetailAddressQueries(
    watch,
    setValue,
    structureQuery
  );

  // Google Places widget
  const { ref: placesRef } = usePlacesWidget({
    apiKey: GOOGLE_API_KEY,
    onPlaceSelected: useCallback(
      place => {
        setValue("latitude", place.geometry.location.lat());
        setValue("longitude", place.geometry.location.lng());
      },
      [setValue]
    ),
    options: {
      types: ["establishment"],
      componentRestrictions: { country: "et" }
    }
  });

  // Effects
  useEffect(() => {
    setHeaderTitle(t("denominations.structures.structureProfile"));
    return () => setHeaderTitle("");
  }, [setHeaderTitle, i18next.language]);

  useEffect(() => {
    setHeaderBackground(false);
    return () => setHeaderBackground(true);
  }, [setHeaderBackground]);

  // Loading and error handling
  if (
    structureQuery.isLoading ||
    addressQueries.countriesQuery.isLoading ||
    isLoadingStructureTypes ||
    isLoadingDenominations ||
    isLoadingChildStructures
  ) {
    return <LoadingScreen />;
  }

  if (!structureQuery.data?.result) {
    return (
      <Box>
        <Typography variant="h6" color="error">
          Structure not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 3 }}>
      <Notification />
      <DeleteModal
        name={structureQuery.data?.result?.name}
        onDelete={handleDelete}
      />
      <DenominationProfieHeader
        denomination={structureQuery.data?.result}
        denominationUpdateMutation={structureMutation}
      />
      <Stack
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        spacing={4}
        sx={{ mt: 4 }}
      >
        <StructureDetailProfileSection
          register={register}
          errors={errors}
          structureTypeOptions={structureTypeOptions}
          parentStructureOptions={parentStructureOptions}
          isLoadingStructureTypes={isLoadingStructureTypes}
          isLoadingDenominations={isLoadingDenominations}
          isCreatedByUser={isCreatedByUser}
          control={control}
        />

        <StructureDetailAddressSection
          register={register}
          setValue={setValue}
          watch={watch}
          errors={errors}
          {...addressQueries}
          placesRef={placesRef}
          isCreatedByUser={isCreatedByUser}
        />

        <StructureDetailDescriptionSection
          register={register}
          errors={errors}
          isCreatedByUser={isCreatedByUser}
        />

        <ChildStructuresSection
          childStructures={childStructures}
          isLoadingChildStructures={isLoadingChildStructures}
          denominationId={denominationId}
        />

        <MutationError
          error={structureMutation.error || structureDeleteMutation.error}
        />

        <StructureDetailFormFooter
          isCreatedByUser={isCreatedByUser}
          structureMutation={structureMutation}
          structureDeleteMutation={structureDeleteMutation}
          openDeleteModal={openDeleteModal}
          reset={reset}
          defaultValues={defaultValues}
          navigate={navigate}
        />
      </Stack>
    </Box>
  );
}
